import React, { useState, useEffect } from "react";
import clsx from "clsx";
import { KTSVG } from "../../../helpers";
import { useLayout } from "../../core";
import { ThemeModeSwitcher } from "../../../partials";
import { DefaultTitle } from "./page-title/DefaultTitle";
import CalendarModal from "../../../../app/modals/CalendarModal";
import NewCustomerModal from "../../../../app/pages/customers/NewCustomerModal";
import NewJobModal from "../../../../app/pages/job-summary/NewJobModal";
import { useEmailPanel } from "../../../../app/pages/mail/contexts/EmailPanelContext";
import { useApprovals } from "../../../../app/contexts/ApprovalsContext";
import { MailIcon } from "../../../../app/pages/mail/components/icons/MailIcon";
import PasswordManagerModal from "../../../../app/modals/PasswordManagerModal";
import { useWebsiteLeads } from "../../../../app/contexts/WebsiteLeadsContext";
import AgentAccessModal from "../../../../app/modals/AgentAccessModal";
import { getAgentAccessConfig } from "../../../../app/pages/marketing/googleAdsApi";
import { getAuth, onIdTokenChanged } from "firebase/auth";

export function HeaderWrapper() {
  const { config, classes, attributes } = useLayout();
  // The legacy chat unread badge (hasUnreadMessage) was retired 2026-06 — it
  // never lit (only ever set false) and the chat drawer it flagged is gone.
  const { openEmailPanel, unreadBadgeCount, activeSyncJob } = useEmailPanel();
  const { openApprovals, count: approvalsCount, flash: approvalsFlash } = useApprovals();
  const { newLeadsCount, openLeadsPanel } = useWebsiteLeads();

  // Log the received unreadBadgeCount whenever it changes
  useEffect(() => {
    console.log("[HeaderWrapper] unreadBadgeCount from context:", unreadBadgeCount);
  }, [unreadBadgeCount]);

  // -----------------------
  // Modal & Email State
  // -----------------------
  const [isCalendarModalVisible, setIsCalendarModalVisible] = useState(false);
  const [isNewCustomerModalVisible, setIsNewCustomerModalVisible] = useState(false);
  const [isNewJobModalVisible, setIsNewJobModalVisible] = useState(false);
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
  const [isAgentAccessModalVisible, setIsAgentAccessModalVisible] = useState(false);
  // Owner-only: the Agent Access tool gates on getAgentAccessConfig (requireOwner),
  // so the header icon shows only for the owner. Same authority the panel uses.
  const [isAgentAccessOwner, setIsAgentAccessOwner] = useState(false);

  // -----------------------
  // Toggle Modal Helpers
  // -----------------------
  const toggleCalendarModal = () => setIsCalendarModalVisible((prev) => !prev);
  const toggleNewCustomerModal = () => setIsNewCustomerModalVisible((prev) => !prev);
  const toggleNewJobModal = () => setIsNewJobModalVisible((prev) => !prev);
  const togglePasswordModal = () => setIsPasswordModalVisible((prev) => !prev);
  const toggleAgentAccessModal = () => setIsAgentAccessModalVisible((prev) => !prev);

  // Resolve owner status once a Firebase user/token exists (authFetch needs one).
  useEffect(() => {
    const unsub = onIdTokenChanged(getAuth(), async (u) => {
      if (!u) { setIsAgentAccessOwner(false); return; }
      try { const r = await getAgentAccessConfig(); setIsAgentAccessOwner(!!r?.ok); }
      catch { setIsAgentAccessOwner(false); }
    });
    return () => unsub();
  }, []);

  // Allow other components (e.g. AsideToolbar) to open the calendar modal via a custom event
  useEffect(() => {
    const handler = () => setIsCalendarModalVisible(true);
    window.addEventListener('open-calendar-modal', handler);
    return () => window.removeEventListener('open-calendar-modal', handler);
  }, []);

  // Approvals inbox "review →" on a pending broker grant deep-links here to open
  // the grant-approval console (TTL + scope review) — never a one-tap approve.
  useEffect(() => {
    const handler = () => setIsAgentAccessModalVisible(true);
    window.addEventListener('approvals:open-agent-access', handler);
    return () => window.removeEventListener('approvals:open-agent-access', handler);
  }, []);

  const [showSyncLogs, setShowSyncLogs] = useState(false);

  // -----------------------
  // Render
  // -----------------------
  return (
    <>
      <div id="kt_header" className={clsx("header", classes.header.join(" "))} {...attributes.headerMenu}>
        {activeSyncJob && (
          <div 
            className="position-absolute top-0 start-0 w-100 h-4px" 
            style={{ zIndex: 1100, backgroundColor: 'rgba(0, 0, 0, 0.05)' }}
          >
            <div 
              className={clsx("h-100", {
                "bg-primary": activeSyncJob.status === 'in_progress',
                "bg-success": activeSyncJob.status === 'completed',
                "bg-danger": activeSyncJob.status === 'error',
                "bg-info": activeSyncJob.status === 'pending'
              })}
              style={{ 
                width: activeSyncJob.status === 'completed' ? '100%' : (activeSyncJob.percent ? `${activeSyncJob.percent}%` : (activeSyncJob.status === 'pending' ? '10%' : '60%')),
                transition: 'width 0.8s ease-in-out' 
              }}
            />
            {activeSyncJob.status !== 'completed' && activeSyncJob.status !== 'error' && (
              <div 
                className="position-absolute top-100 start-50 translate-middle-x badge badge-light-primary shadow-sm mt-1 py-1 px-3 fw-bold cursor-pointer"
                style={{ fontSize: '10px', whiteSpace: 'nowrap', border: '1px solid #eee', zIndex: 1101 }}
                onClick={() => setShowSyncLogs(!showSyncLogs)}
              >
                <span className="spinner-border spinner-border-sm me-2" style={{ width: '12px', height: '12px', borderWidth: '2px' }}></span>
                {activeSyncJob.progress || 'Syncing Mail...'}
                <i className={clsx("ms-2 fas", showSyncLogs ? "fa-chevron-up" : "fa-chevron-down")}></i>
              </div>
            )}
            
            {showSyncLogs && activeSyncJob.logs && activeSyncJob.logs.length > 0 && (
              <div 
                className="position-absolute top-100 start-50 translate-middle-x mt-8 bg-white border rounded shadow-lg p-3"
                style={{ width: '350px', maxHeight: '250px', overflowY: 'auto', zIndex: 1102, fontSize: '11px' }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2 border-bottom pb-1">
                  <span className="fw-bolder text-uppercase text-muted" style={{ fontSize: '9px' }}>Live Sync Logs</span>
                  <button className="btn btn-icon btn-sm btn-active-light-primary" onClick={() => setShowSyncLogs(false)}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                {activeSyncJob.logs.slice().reverse().map((log: string, idx: number) => (
                  <div key={idx} className="mb-1 text-dark family-monospace border-bottom border-light pb-1" style={{ fontSize: '10px' }}>
                    {log}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className={clsx(classes.headerContainer.join(" "), "pt-0 pt-lg-3")}>
          {/* Main header row */}
          <div className="d-flex align-items-center justify-content-between">
            {/* Left side: Mobile toggle and Title */}
            <div className="d-flex align-items-center">
              {/* Standard Metronic Mobile Aside Toggle Button - ADDED */}
              {/* This button is responsible for toggling the main sidebar (Aside) on mobile */}
              {/* It's shown only on smaller screens (d-lg-none) */}
              <div className="d-flex align-items-center d-lg-none me-3"> {/* me-3 for spacing */}
                <button
                  className="btn btn-icon btn-active-light-primary w-30px h-30px w-md-40px h-md-40px"
                  id="kt_aside_mobile_toggle" // Standard Metronic ID for the aside toggle
                  title="Toggle Aside"
                >
                  <KTSVG path='/media/icons/duotune/text/txt001.svg' className='svg-icon-1' />
                </button>
              </div>

              <DefaultTitle />
            </div>

            {/* Right side: All action buttons */}
            <div className="d-flex align-items-center">
            <ThemeModeSwitcher toggleBtnClass="btn-active-light-primary w-30px h-30px w-md-40px h-md-40px" />
            
            {/* Desktop Icons Group */}
            <div className="d-none d-lg-flex align-items-center">
              <div
                className="btn btn-sm btn-icon btn-icon-muted btn-active-icon-primary ms-2"
                onClick={toggleNewJobModal}
                title="Add New Job"
                style={{ cursor: "pointer" }}
              >
                <KTSVG path="/media/icons/duotune/files/fil005.svg" className="svg-icon-2x" />
              </div>
              <div
                className="btn btn-sm btn-icon btn-icon-muted btn-active-icon-primary ms-2"
                onClick={toggleNewCustomerModal}
                title="Add New Customer"
                style={{ cursor: "pointer" }}
              >
                <KTSVG path="/media/icons/duotune/communication/com014.svg" className="svg-icon-2x" />
              </div>
              <div
                className="btn btn-sm btn-icon btn-icon-muted btn-active-icon-primary ms-2"
                onClick={toggleCalendarModal}
                title="Calendar"
                style={{ cursor: "pointer" }}
              >
                <KTSVG path="/media/icons/duotune/general/gen014.svg" className="svg-icon-2x" />
              </div>
              {/* Password Manager Button */}
              <div
                className="btn btn-sm btn-icon btn-icon-muted btn-active-icon-primary ms-2"
                onClick={togglePasswordModal}
                title="Password Manager"
                style={{ cursor: "pointer" }}
              >
                <KTSVG path="/media/icons/custom/lock.svg" className="svg-icon-2x" />
              </div>

              {/* Agent Access (owner-only) — manage external-agent access (Google Ads + CRM/billing/email) */}
              {isAgentAccessOwner && (
                <div
                  className="btn btn-sm btn-icon btn-icon-muted btn-active-icon-primary ms-2"
                  onClick={toggleAgentAccessModal}
                  title="Agent Access"
                  style={{ cursor: "pointer" }}
                >
                  <i className="fas fa-robot" style={{ fontSize: '1.2rem' }}></i>
                </div>
              )}

              {/* LLM Chat Button (now before Email Feed) */}
              <div className='d-flex align-items-center ms-1 ms-lg-3'> 
                  <div
                    className='btn btn-icon btn-active-light-primary btn-custom position-relative w-30px h-30px w-md-40px h-md-40px' 
                    id='kt_drawer_chat_toggle' 
                  >
                    <KTSVG
                      path='/media/icons/duotune/communication/com012.svg'
                      className='svg-icon-1'
                    />
                  </div>
              </div>

              {/* Website Leads Button */}
              <div
                className="btn btn-sm btn-icon btn-icon-muted btn-active-icon-primary position-relative ms-2"
                onClick={openLeadsPanel}
                title="Website Activity"
                style={{ cursor: "pointer" }}
              >
                <i className="fas fa-satellite-dish" style={{ fontSize: '1.2rem' }}></i>
                {newLeadsCount > 0 && (
                  <span
                    className="position-absolute badge badge-circle badge-warning"
                    style={{
                      top: "-8px",
                      right: "-8px",
                      minWidth: "20px",
                      height: "20px",
                      fontSize: "11px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {newLeadsCount}
                  </span>
                )}
              </div>

              {/* Email Feed Button (now after LLM Chat) */}
              <div
                className="btn btn-sm btn-icon btn-icon-muted btn-active-icon-primary position-relative ms-2"
                onClick={() => openEmailPanel('feed')}
                title="View Email Feed"
                style={{ cursor: "pointer" }}
              >
                <MailIcon className="svg-icon-2x" />
                {unreadBadgeCount > 0 && (
                  <span
                    className="position-absolute badge badge-circle badge-primary"
                    style={{
                      top: "-8px",
                      right: "-8px",
                      minWidth: "20px",
                      height: "20px",
                      fontSize: "11px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {unreadBadgeCount}
                  </span>
                )}
              </div>

              {/* Approvals inbox — unified pending agent proposals (GAds, accounting, drafts) */}
              <div
                className="btn btn-sm btn-icon btn-icon-muted btn-active-icon-primary position-relative ms-2"
                onClick={openApprovals}
                title="Approvals inbox — pending agent proposals"
                style={{ cursor: "pointer" }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                  <path d="M9 11l3 3L22 4" />
                </svg>
                {approvalsCount > 0 && (
                  <span
                    className={`position-absolute badge badge-circle ${approvalsFlash ? "badge-danger" : "badge-warning"}`}
                    style={{
                      top: "-8px",
                      right: "-8px",
                      minWidth: "20px",
                      height: "20px",
                      fontSize: "11px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {approvalsCount}
                  </span>
                )}
              </div>
            </div>

            {/* Mobile Hamburger Menu */}
            <div className="d-flex d-lg-none ms-2 align-items-center">
                <div 
                    className="btn btn-icon btn-active-light-primary w-30px h-30px w-md-40px h-md-40px"
                    data-kt-menu-trigger="click"
                    data-kt-menu-attach="parent"
                    data-kt-menu-placement="bottom-end"
                >
                    <KTSVG path="/media/icons/duotune/abstract/abs015.svg" className="svg-icon-1" />
                    {unreadBadgeCount > 0 && (
                         <span className='bullet bullet-dot bg-danger h-6px w-6px position-absolute translate-middle top-0 start-100 animation-blink'></span>
                    )}
                </div>
                
                <div 
                  className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg menu-state-primary fw-bold py-4 fs-6 w-200px" 
                  data-kt-menu="true"
                >
                    {/* New Job */}
                    <div className="menu-item px-3">
                        <a href="#" className="menu-link px-3" onClick={(e) => { e.preventDefault(); toggleNewJobModal(); }}>
                            <span className="menu-icon"><KTSVG path="/media/icons/duotune/files/fil005.svg" className="svg-icon-2" /></span>
                            <span className="menu-title">New Job</span>
                        </a>
                    </div>
                    {/* New Customer */}
                    <div className="menu-item px-3">
                        <a href="#" className="menu-link px-3" onClick={(e) => { e.preventDefault(); toggleNewCustomerModal(); }}>
                            <span className="menu-icon"><KTSVG path="/media/icons/duotune/communication/com014.svg" className="svg-icon-2" /></span>
                            <span className="menu-title">New Customer</span>
                        </a>
                    </div>
                     {/* Calendar */}
                    <div className="menu-item px-3">
                        <a href="#" className="menu-link px-3" onClick={(e) => { e.preventDefault(); toggleCalendarModal(); }}>
                            <span className="menu-icon"><KTSVG path="/media/icons/duotune/general/gen014.svg" className="svg-icon-2" /></span>
                            <span className="menu-title">Calendar</span>
                        </a>
                    </div>
                    {/* Password Manager */}
                    <div className="menu-item px-3">
                        <a href="#" className="menu-link px-3" onClick={(e) => { e.preventDefault(); togglePasswordModal(); }}>
                            <span className="menu-icon"><KTSVG path="/media/icons/custom/lock.svg" className="svg-icon-2" /></span>
                            <span className="menu-title">Passwords</span>
                        </a>
                    </div>
                    {/* Agent Access (owner-only) */}
                    {isAgentAccessOwner && (
                      <div className="menu-item px-3">
                          <a href="#" className="menu-link px-3" onClick={(e) => { e.preventDefault(); toggleAgentAccessModal(); }}>
                              <span className="menu-icon"><i className="fas fa-robot svg-icon-2"></i></span>
                              <span className="menu-title">Agent Access</span>
                          </a>
                      </div>
                    )}
                     <div className="separator my-2"></div>
                     
                     {/* Chat */}
                    <div className="menu-item px-3">
                        <a href="#" className="menu-link px-3" onClick={(e) => { e.preventDefault(); document.getElementById('kt_drawer_chat_toggle')?.click(); }}>
                            <span className="menu-icon">
                                <KTSVG path='/media/icons/duotune/communication/com012.svg' className='svg-icon-2' />
                            </span>
                            <span className="menu-title">Chat</span>
                        </a>
                    </div>
                    
                    {/* Website Leads */}
                    <div className="menu-item px-3">
                        <a href="#" className="menu-link px-3" onClick={(e) => { e.preventDefault(); openLeadsPanel(); }}>
                            <span className="menu-icon">
                                <i className="fas fa-satellite-dish svg-icon-2"></i>
                            </span>
                            <span className="menu-title">Website Activity</span>
                            {newLeadsCount > 0 && <span className="badge badge-light-warning fs-8 fw-bolder ms-2">{newLeadsCount}</span>}
                        </a>
                    </div>

                     {/* Email */}
                    <div className="menu-item px-3">
                        <a href="#" className="menu-link px-3" onClick={(e) => { e.preventDefault(); openEmailPanel('feed'); }}>
                            <span className="menu-icon">
                                <MailIcon className="svg-icon-2" />
                            </span>
                            <span className="menu-title">Email</span>
                            {unreadBadgeCount > 0 && <span className="badge badge-light-primary fs-8 fw-bolder ms-2">{unreadBadgeCount}</span>}
                        </a>
                    </div>
                </div>
            </div>
            
          </div> {/* END OF YOUR ORIGINAL TOP BAR ICONS */}
          </div> {/* END OF MAIN HEADER ROW */}

        </div>
      </div>

      {/* Modals */}
      {isCalendarModalVisible && <CalendarModal onClose={toggleCalendarModal} />}
      {isNewCustomerModalVisible && <NewCustomerModal show={isNewCustomerModalVisible} onClose={toggleNewCustomerModal} customer={null} />}
      {isNewJobModalVisible && <NewJobModal show={isNewJobModalVisible} onClose={toggleNewJobModal} />}
      {isPasswordModalVisible && <PasswordManagerModal onClose={togglePasswordModal} />}
      {isAgentAccessModalVisible && <AgentAccessModal onClose={toggleAgentAccessModal} />}
    </>
  );
}